import React, { useEffect, useState, useRef } from 'react';
import { Package, RefreshCw, Save, TrendingUp, Filter, Search, X, FileText, AlertTriangle, DollarSign, Activity } from 'lucide-react';
import inventoryManagementService from '../services/inventoryManagementService';
import InventoryDashboard from '../components/InventoryDashboard';
import ProductDetailsModal from '../components/ProductDetailsModal';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// Determinar salud del inventario basado en días de rotación (no en valor absoluto)
// Modelo de negocio: $500M/mes, ciclo 22 días (15 lead time + 7 transporte), pedidos semanales
// Inventario óptimo: 22-30 días. Sobre-stock detecta capital mal asignado que debe redistribuirse.
const getStationaryMoneyColor = (inventoryDays) => {
    if (!inventoryDays || inventoryDays <= 0) {
        return { text: 'text-gray-600', severity: 'SIN DATOS' };
    }
    // CRÍTICO: Más de 45 días = sobre-stock severo, capital desperdiciado
    if (inventoryDays > 45) {
        return { text: 'text-red-600', severity: 'SOBRE-STOCK' };
    }
    // ADVERTENCIA: 31-45 días = exceso moderado, revisar redistribución
    if (inventoryDays > 30) {
        return { text: 'text-yellow-600', severity: 'EXCESO' };
    }
    // ÓPTIMO: 22-30 días = cubre ciclo + buffer semanal
    if (inventoryDays >= 22) {
        return { text: 'text-green-600', severity: 'ÓPTIMO' };
    }
    // RIESGO: <22 días = no cubre el ciclo, riesgo de quiebre
    return { text: 'text-orange-600', severity: 'BAJO' };
};

const InventoryManagementPage = () => {
    const { user, hasPermission } = useAuth();
    const showFinancials = hasPermission(['admin']);

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Estados para matriz
    const [groupedProducts, setGroupedProducts] = useState({});
    const [categories, setCategories] = useState([]);

    // Estados para filtros y búsqueda
    const [searchTerm, setSearchTerm] = useState('');
    const [abcFilter, setAbcFilter] = useState('');
    const [supplierFilter, setSupplierFilter] = useState(''); // Nuevo filtro de proveedor

    // Estados para análisis y modal
    const [analyzing, setAnalyzing] = useState(false);
    const [coverageDays, setCoverageDays] = useState(15);
    const [leadTime, setLeadTime] = useState(7); // Nuevo estado para Tiempo de Entrega
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // Estados para detalle de categoría
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [selectedCategoryData, setSelectedCategoryData] = useState({ name: '', items: [] });

    useEffect(() => {
        fetchInventory();
    }, []); // Carga inicial

    const fetchInventory = async () => {
        try {
            setLoading(true);
            const response = await inventoryManagementService.getView();
            console.log('📦 Inventory Data Received:', response); // DEBUG

            let data = [];
            if (response.success && Array.isArray(response.data)) {
                data = response.data;
            } else if (Array.isArray(response)) {
                data = response;
            } else {
                console.error('Formato de datos inesperado:', response);
                setError('Error: Formato de datos incorrecto');
                setLoading(false);
                return;
            }

            setProducts(data);
            organizeProductsForInventory(data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching inventory:', err);
            setError('Error cargando inventario');
            setLoading(false);
        }
    };

    const handleOpenCategoryDetail = (catName) => {
        const catData = groupedProducts[catName];
        if (!catData) return;

        const items = [];
        Object.entries(catData).forEach(([subgroup, presentations]) => {
            Object.values(presentations).forEach(flavors => {
                Object.values(flavors).forEach(p => {
                    const stock = Number(p.current_stock || 0);
                    const ivaFactor = 1 + (Number(p.taxes_iva || 19) / 100);
                    const priceNoVat = Number(p.standard_price || 0) / ivaFactor;
                    const cost = Number(p.purchasing_price || 0) || (Number(p.standard_price || 0) / ivaFactor);
                    const avgDaily = Number(p.avg_daily_consumption || 0);

                    // Rentabilidad Operativa (GMROI)
                    const unitMargin = priceNoVat - cost;
                    const projectedMonthlyProfit = unitMargin * (avgDaily * 30);
                    const investment = stock * cost;

                    const gmroi = investment > 0 ? (projectedMonthlyProfit / investment) * 100 : 0;
                    const stockDays = avgDaily > 0 ? stock / avgDaily : null;

                    // Dinero Estacionado vs Déficit
                    const idealStock = avgDaily * (leadTime + coverageDays);
                    const excessQty = Math.max(0, stock - idealStock);
                    const deficitQty = Math.max(0, idealStock - stock);

                    const stationaryMoney = excessQty * cost;
                    const deficitMoney = deficitQty * cost;

                    items.push({
                        ...p,
                        costNet: cost,
                        inventoryValue: investment,
                        gmroi,
                        stockDays,
                        stationaryMoney,
                        deficitMoney,
                        excessQty,
                        deficitQty
                    });
                });
            });
        });

        // Ordenar por relevancia financiera (Estacionado + Déficit)
        items.sort((a, b) => (b.stationaryMoney + b.deficitMoney) - (a.stationaryMoney + a.deficitMoney));

        setSelectedCategoryData({ name: catName, items });
        setShowCategoryModal(true);
    };

    // Obtener lista única de proveedores para el filtro
    const uniqueSuppliers = [...new Set(products.map(p => p.supplier).filter(Boolean))].sort();

    // --- HELPER FUNCTIONS (Adaptadas de InventoryBillingPage) ---

    const extractPresentation = (productName, productCode) => {
        // Lógica Especial: Productos MEL (leches) van en fila aparte "LECHE"
        // Lógica Especial: Productos MEL (leches) van en fila aparte "LECHE"
        if (productCode && String(productCode).toUpperCase().startsWith('MEL')) {
            return 'LECHE (MEZCLA B)';
        }

        const normalized = productName.toUpperCase().replace(/\s+/g, ' ');

        // Regla explícita para LIBRA
        if (normalized.includes('LIBRA')) {
            return 'LIBRA';
        }

        let match = normalized.match(/(?:\bX\b\s*)?(\d+(?:\.\d+)?)\s*(ML|GRS|GR?|KG|L|G|OZ|UND|UNIDADES)\b/i);
        if (!match) {
            match = normalized.match(/(\d+(?:\.\d+)?)\s*(ML|GRS|GR?|KG|L|G|OZ|UND|UNIDADES)\b/i);
        }

        if (match) {
            let value = match[1];
            const unitToken = match[2].toUpperCase();
            let unit = 'G';
            if (unitToken === 'ML' || unitToken === 'L' || normalized.includes('ML') || normalized.includes('SIROPE') || normalized.includes('LIQUIDO')) {
                unit = 'ML';
            } else if (unitToken === 'OZ') {
                unit = 'OZ';
            } else if (unitToken === 'UND' || unitToken === 'UNIDADES') {
                unit = 'UND';
            }

            if (unitToken === 'L') {
                const n = parseFloat(value);
                if (!Number.isNaN(n)) value = String(Math.round(n * 1000));
            }

            // Normalización de GRS a G
            if (unitToken === 'GRS') unit = 'G';

            if (value === '250') return unit === 'ML' ? '250ML' : (unit === 'UND' ? '250UND' : '250G');
            // Unificación: 330 -> 350 para consolidar presentaciones comerciales similares
            if (value === '330') return unit === 'ML' ? '350ML' : '350G';
            if (value === '350') return unit === 'ML' ? '350ML' : '350G';
            if (value === '360') return unit === 'ML' ? '360ML' : '360G';
            if (value === '500') return unit === 'ML' ? '500ML' : (unit === 'UND' ? '500UND' : '500G');
            if (value === '1000') return unit === 'ML' ? '1000ML' : (unit === 'UND' ? '1000UND' : '1000G');
            if (value === '1100') return unit === 'ML' ? '1100ML' : '1100G';
            if (value === '1150') return unit === 'ML' ? '1150ML' : '1150G';
            if (value === '2300') return unit === 'ML' ? '2300ML' : '2300G';
            if (value === '3000') return unit === 'ML' ? '3000ML' : (unit === 'UND' ? '3000UND' : '3000G'); // Agregado para Gomas 3000g
            if (value === '3400') return unit === 'ML' ? '3400ML' : '3400G';

            // Retorno genérico si no cayó en reglas específicas
            return `${value}${unit}`;
        }

        const aux = normalized.match(/\b(250|500|750|1000|1200)\b/);
        if (aux && (normalized.includes('SIROPE') || normalized.includes('LIQUIDO'))) {
            return `${aux[1]}ML`;
        }

        return 'STANDARD';
    };

    const extractFlavor = (productName, subgroupContext = '') => {
        const upperName = productName.toUpperCase();
        const normalized = upperName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();

        if (normalized.includes('LIMA LIMON') || normalized.includes('LIMA-LIMON') || normalized.includes('LIMA/LIMON')) {
            return 'LIMA LIMON';
        }

        // Limpieza para Productos Especiales
        if (normalized.includes('GRANIZADO SACHET')) {
            return normalized.replace('GRANIZADO SACHET', '').trim();
        }
        if (normalized.includes('FLAUTIRICOS')) return 'FLAUTIRICOS';
        if (normalized.includes('MICHELON')) return normalized.replace('MICHELON', '').trim();
        if (normalized.includes('MERENGONES')) return 'MERENGONES';
        if (normalized.includes('ORINGS')) return 'ORINGS';

        if (normalized.includes('CHOCOLATE') && (normalized.includes('AVELLANA') || normalized.includes('AVELLANAS'))) {
            return 'CHOCOLATE AVELLANA';
        }

        if (normalized.includes('CHOCOLATE') && (normalized.includes('SUIZO') || normalized.includes('ZUIZO'))) {
            return 'CHOCOLATE SUIZO';
        }

        if (normalized.includes('AZUCAR') && normalized.includes('MARACUYA')) {
            return 'AZUCAR MARACUYA';
        }

        // Regla específica para CHUPETAS LABIOS ROJOS (Override)
        if (normalized.includes('CHUPETA') && normalized.includes('LABIOS')) {
            return 'CHUPETAS DE LABIOS ROJOS';
        }

        // Regla específica para MATERIAL DE EMPAQUE (Diferenciadores Clave)
        // Regla específica para MATERIAL DE EMPAQUE (Diferenciadores Clave)
        if (normalized.includes('TAPA')) {
            if (normalized.includes('GOLD')) return 'TAPA GOLD';
            if (normalized.includes('DOMO')) return 'TAPA CONTENEDOR';
            if (normalized.includes('PLANA')) return 'TAPA PLANA';
            if (normalized.includes('EASY OPEN')) return 'EASY OPEN';
            if (normalized.includes('ALUMINIO')) return 'ALUMINIO';
            if (normalized.includes('CUADRADA')) return 'CUADRADA';
            if (normalized.includes('SILICONA')) return 'SILICONA';
            if (normalized.includes('NEGRA')) return 'TAPA NEGRA';
            if (normalized.includes('BLANCA')) return 'TAPA BLANCA';
            return 'TAPA BOWL';
        }
        if (normalized.includes('CUBETA')) return 'CUBETA';
        if (normalized.includes('BOWL')) return 'BOWL';
        if (normalized.includes('DIGITAL')) return 'DIGITAL';
        if (normalized.includes('LOGO PROPIO') || normalized.includes('PROPIO')) return 'PROPIO';
        if (normalized.includes('BLANCO')) return 'BLANCO';
        if (/\bPET\b/.test(normalized)) return 'PET';
        if (normalized.includes('PP') && !normalized.includes('TOPF')) return 'PP'; // Evitar TOPF si es marca

        // Regla específica para yogurt griego/natural con alulosa
        if (normalized.includes('YOGURT') && normalized.includes('ALULOSA')) {
            if (normalized.includes('GRIEGO')) return 'GRIEGO CON ALULOSA';
            if (normalized.includes('NATURAL')) return 'NATURAL CON ALULOSA';
            return 'YOGURT ALULOSA';
        }

        // Regla específica para LECHE: Separar por tipo antes de agrupar por ALULOSA
        if (normalized.includes('LECHE')) {
            let tipo = '';
            if (normalized.includes('SUAVE')) tipo = 'SUAVE';
            else if (normalized.includes('YOGURT')) tipo = 'YOGURT';
            else if (normalized.includes('PREMIUM')) tipo = 'PREMIUM';
            else if (normalized.includes('GELATO')) tipo = 'GELATO';
            else if (normalized.includes('FROZZEN')) tipo = 'FROZZEN';

            if (tipo) {
                // Si tiene alulosa, agregamos el sufijo para diferenciar
                if (normalized.includes('ALULOSA')) {
                    return `${tipo} ALULOSA`;
                }
                return tipo;
            }
        }

        // Regla específica para MEZCLAS con VAINILLA: Separar variantes específicas
        if ((normalized.includes('MEZCLA') || normalized.includes('MALTEADA')) &&
            (normalized.includes('VAINILLA') || normalized.includes('VANILLA'))) {

            // Detectar variantes específicas primero
            if (normalized.includes('FRANCESA')) return 'VAINILLA FRANCESA';
            if (normalized.includes('AMERICANA')) return 'VAINILLA AMERICANA';
            if (normalized.includes('DESCREMADA')) return 'VAINILLA DESCREMADA';
            if (normalized.includes('NARANJA')) return 'VAINILLA NARANJA';
            if (normalized.includes('GELATO')) return 'VAINILLA GELATO';
            if (normalized.includes('ALULOSA')) return 'VAINILLA ALULOSA';

            // Si no tiene variante específica, retornar VAINILLA normal
            return 'VAINILLA';
        }

        // Regla explícita para diferenciar GEL de CHAMOY (en gr) de Salsas (en ml)
        if (normalized.includes('GEL') && normalized.includes('CHAMOY')) {
            return 'GEL CHAMOY';
        }

        // Regla para consolidar PERLAS CHICLE / CHICLES / EXPLOSIVAS CHICLE
        if ((normalized.includes('PERLA') || subgroupContext.includes('PERLAS')) && normalized.includes('CHICLE')) {
            return 'CHICLE';
        }

        // Regla específica para CHOCOLATE, GIRASOL, LENTEJUELAS, CHICLE en TOPPINGS o todo el subgrupo TOPPINGS DULCE, TOPPING GOMAS y TOPPING IMPORTADOS
        if (normalized.includes('CHOCOLATE') || normalized.includes('GIRASOL') || normalized.includes('LENTEJUELAS') || normalized.includes('CHICLE') ||
            subgroupContext === 'TOPPINGS DULCE' || subgroupContext === 'TOPPING GOMAS' || subgroupContext === 'TOPPING IMPORTADOS') {
            // Limpiar patrones de peso/unidades
            let cleaned = normalized.replace(/\s+(?:X\s*)?\d+(?:\.\d+)?\s*(?:ML|GRS|GR?|KG|L|G|OZ|UND|UNIDADES)\b/g, '').trim();
            cleaned = cleaned.replace(/\s+\d+$/g, '').trim(); // Numeros sueltos al final

            // Quitar palabras de ruido comunes al inicio si existen
            cleaned = cleaned.replace(/^(TOPPING|SALSA|MEZCLA)\s+/, '');

            // Si el resultado es muy corto (solo CHOCOLATE), retornarlo asi.
            // Si es mas largo (CHOCOLATE BLANCO, CHOCOLATE CORAZONES), devolverlo todo.
            return cleaned;
        }

        const commonFlavors = [
            'BLUEBERRY', 'CAFE', 'CEREZA', 'CHAMOY', 'COCO', 'FRESA',
            'ICE PINK', 'LYCHE', 'MANGO BICHE CON SAL', 'MANGO BICHE', 'MANZANA VERDE',
            'MARACUYA', 'SANDIA', 'VAINILLA', 'VANILLA', 'UVA', 'LIMA LIMON', 'LIMON',
            'NARANJA', 'PIÑA', 'MENTA', 'GOMITAS', 'GOMITA', 'MANZANA'
        ];
        // Removed CHICLE from commonFlavors to allow the detailed rule above to catch it.

        for (const flavor of commonFlavors) {
            if (normalized.includes(flavor)) {
                if (flavor === 'GOMITA') return 'GOMITAS';
                if (flavor === 'MANZANA') return 'MANZANA VERDE';
                if (flavor === 'VANILLA') return 'VAINILLA';
                if (flavor === 'LIMON' && !normalized.includes('LIMA')) return 'LIMA LIMON';
                if (flavor === 'MANGO BICHE' && !normalized.includes('SAL')) return 'MANGO BICHE';
                return flavor;
            }
        }

        // Regla específica para PITILLOS: Extraer dimensión (ej: 7 MM)
        if (normalized.includes('PITILLO')) {
            const pitilloMatch = normalized.match(/(\d+)\s*MM/);
            if (pitilloMatch) {
                return `PITILLOS ${pitilloMatch[1]} MM`;
            }
            return 'PITILLOS';
        }

        const m = normalized.match(/SABOR(?:\s+A)?\s+([A-ZÁÉÍÓÚÑ ]+?)(?:\s+X\s*\d|$)/);
        if (m && m[1]) return m[1].trim();

        // Limpieza mejorada: Remover patrones de peso con o sin "X"
        // Ej: " X 500ML", " 1100GR", " 300G", " 40 UND", " 3000 GRS"
        let cleaned = normalized.replace(/\s+(?:X\s*)?\d+(?:\.\d+)?\s*(?:ML|GRS|GR?|KG|L|G|OZ|UND|UNIDADES)\b/g, '').trim();

        // Si después de limpieza quedan solo números al final, quitarlos (ej: "SABOR 1100")
        cleaned = cleaned.replace(/\s+\d+$/g, '').trim();

        const words = cleaned.split(/\s+/);
        let candidate = words[words.length - 1] || 'CLASICO';
        const unitTokens = new Set(['ML', 'GR', 'G', 'KG', 'L', 'OZ', 'X']);
        if (unitTokens.has(candidate)) candidate = words[words.length - 2] || 'CLASICO';

        if (candidate === 'GOMITA') return 'GOMITAS';
        if (candidate === 'MANZANA') return 'MANZANA VERDE';
        if (candidate === 'MM') return 'PITILLOS'; // Fallback por si acaso
        if (candidate === 'UNI') return 'TOPF NATURAL';

        return candidate;
    };

    const pickFlavor = (product) => {
        try {
            const sub = String(product?.subcategory || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
            if (sub) {
                if (sub.includes('LIMA LIMON') || sub.includes('LIMA-LIMON') || sub.includes('LIMA/LIMON')) return 'LIMA LIMON';
                return sub;
            }
        } catch (_) { }
        return extractFlavor(product?.name || '');
    };

    const organizeProductsForInventory = (productsList) => {
        const grouped = {};
        const cats = new Set(); // Guardará los GRUPOS (Nivel 1)
        let skippedCount = 0;

        console.log(`🧩 Organizing ${productsList.length} products (Improved V2)...`);

        productsList.forEach(product => {
            if (!product.name) {
                skippedCount++;
                return;
            }

            // 0. Filtros globales de exclusión (Servicios, Domicilios, etc)
            if (product.internal_code === 'DO' || product.name === 'DOMICILIOS' || product.type === 'Service') {
                return;
            }

            // 1. Obtener Grupo y Subgrupo desde BD (Ahora usamos las columnas estándar)
            let group = (product.category || 'SIN CLASIFICAR').toUpperCase().trim();
            let subgroup = (product.subcategory || 'GENERAL').toUpperCase().trim();

            // Override específico solicitado por el usuario
            if (product.internal_code === 'MEP26' || product.internal_code === 'MES26') {
                subgroup = 'HELADO PREMIUM';
            }
            if (product.name && product.name.toUpperCase().includes('FINI')) {
                subgroup = 'TOPPING IMPORTADOS';
            }

            // Regla dinámica para TOPPINGS basada en Código Interno (TO + X)
            if (product.internal_code && product.internal_code.startsWith('TO')) {
                group = 'TOPPING';
                if (product.internal_code.startsWith('TOI')) subgroup = 'TOPPING IMPORTADOS';
                else if (product.internal_code.startsWith('TOD')) subgroup = 'TOPPINGS DULCE';
                else if (product.internal_code.startsWith('TOC')) subgroup = 'TOPPING CHOCOLATE';
            }

            if (product.internal_code === 'TOD07') {
                subgroup = 'TOPPINGS DULCE';
            }

            // Override para Sirope Geniality y Bases Cítricas (Liquimon)
            if (subgroup === 'SIROPE GENIALITY' || (product.name && product.name.toUpperCase().includes('LIQUIMON'))) {
                group = 'GENIALITY';
            }

            // NUEVA LÓGICA DE MEZCLAS (Solicitud Usuario)
            const cleanCode = (product.internal_code || '').trim().toUpperCase();
            const upperName = (product.name || '').toUpperCase();

            if (group === 'MEZCLAS EN POLVO' || cleanCode.startsWith('ME')) {
                // 1. GRANIZADOS
                if (cleanCode.startsWith('MEG') || upperName.includes('GRANIZADO')) {
                    group = 'GRANIZADOS';

                    // Clasificación detallada según tabla proporcionada
                    if (['MEG14', 'MEG10', 'MEG08'].includes(cleanCode)) subgroup = 'CREMOSO';
                    else if (cleanCode === 'MEG12') subgroup = 'BASE';
                    else if (['MEG11', 'MEG05', 'MEG04', 'MEG02', 'MEG01'].includes(cleanCode)) subgroup = 'CITRICO';
                    else if (['MEG09', 'MEG07', 'MEG06', 'MEG03'].includes(cleanCode)) subgroup = 'DULCE';
                    else subgroup = 'OTROS GRANIZADOS';
                }
                // 2. HELADOS (Mezcla A y B)
                else if (cleanCode.startsWith('MES') || cleanCode.startsWith('MEY') || cleanCode.startsWith('MEP') || cleanCode.startsWith('MEL') || cleanCode.startsWith('MED')) {
                    group = 'HELADOS';
                }
                // 3. OTRAS MEZCLAS (Malteadas, Chantilly, etc)
                else {
                    group = 'OTRAS MEZCLAS';
                    if (cleanCode.startsWith('MMM') || cleanCode.startsWith('MMP')) subgroup = 'MALTEADAS';
                    else if (cleanCode.startsWith('MEC')) subgroup = 'CHANTILLY';
                    else if (cleanCode.startsWith('MEM')) subgroup = 'ROLL FACTORY';
                }
            }

            // Guardar para uso en KPIs (Dashboard)
            product._assigned_group = group;
            product._assigned_subgroup = subgroup;

            // 2. Filtros
            if (abcFilter && product.abc_classification !== abcFilter) return;
            if (supplierFilter && product.supplier !== supplierFilter) return;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                const match = product.name.toLowerCase().includes(term) ||
                    product.internal_code?.toLowerCase().includes(term) ||
                    product.barcode?.toLowerCase().includes(term) ||
                    group.toLowerCase().includes(term) ||
                    subgroup.toLowerCase().includes(term);
                if (!match) return;
            }

            // 3. Extracción de Dimensiones para la Matriz
            const presentation = extractPresentation(product.name, product.internal_code);

            // Para el "Sabor" (Columna), usamos el nombre simplificado o el internal_code para unicidad si es necesario
            let flavor = extractFlavor(product.name, subgroup);

            // Estructura Anidada: Grupo -> Subgrupo -> Presentación -> Sabor -> Producto
            if (!grouped[group]) grouped[group] = {};
            if (!grouped[group][subgroup]) grouped[group][subgroup] = {};
            if (!grouped[group][subgroup][presentation]) grouped[group][subgroup][presentation] = {};

            // COLLISION HANDLING: Si la celda ya está ocupada por OTRO producto, intentar hacer el Sabor Único
            // Usamos internal_code para verificar si es el mismo producto (no debería pasar, pero por seguridad)
            if (grouped[group][subgroup][presentation][flavor] &&
                grouped[group][subgroup][presentation][flavor].internal_code !== product.internal_code) {

                // Existe colisión con otro producto.
                // Opción: Agregar sufijo para diferenciar
                let i = 2;
                let newFlavor = `${flavor} (${i})`;
                while (grouped[group][subgroup][presentation][newFlavor]) {
                    i++;
                    newFlavor = `${flavor} (${i})`;
                }
                flavor = newFlavor;
            }

            grouped[group][subgroup][presentation][flavor] = product;
            cats.add(group);

        });

        // 4. INYECCIÓN DE LECHES (MELCODES) EN SUBGRUPOS ESPECÍFICOS
        const MILK_MAPPING = {
            'MEL01': ['HELADO SUAVE'],
            'MEL02': ['HELADO YOGURT', 'YOGURT FROZEN'],
            'MEL03': ['HELADO PREMIUM'],
            'MEL06': ['YOGUR SIN AZUCAR', 'YOGURT GRIEGO'],
            'MEL07': ['SUAVE SIN AZUCAR']
        };

        productsList.forEach(product => {
            if (!product.internal_code || !product.internal_code.startsWith('MEL')) return;

            const targetSubgroups = MILK_MAPPING[product.internal_code];
            if (!targetSubgroups) return;

            targetSubgroups.forEach(targetSubgroup => {
                // Buscar en todos los grupos si existe este subgrupo
                Object.keys(grouped).forEach(groupName => {
                    if (grouped[groupName][targetSubgroup]) {
                        // Inyectar la leche
                        const milkPres = 'LECHE BASE';
                        const milkFlavor = `LECHE (${product.internal_code})`; // Nombre para que sea único

                        if (!grouped[groupName][targetSubgroup][milkPres]) {
                            grouped[groupName][targetSubgroup][milkPres] = {};
                        }

                        // Solo inyectar si no existe ya para evitar duplicados visuales confusos
                        if (!grouped[groupName][targetSubgroup][milkPres][milkFlavor]) {
                            grouped[groupName][targetSubgroup][milkPres][milkFlavor] = product;
                        }
                    }
                });
            });
        });

        console.log(`✅ Organization complete. Groups found: ${cats.size}`);
        setGroupedProducts(grouped);
        setCategories([...cats].sort());
    };

    // Re-organizar cuando cambian filtros
    useEffect(() => {
        if (products.length > 0) {
            organizeProductsForInventory(products);
        }
    }, [searchTerm, abcFilter, supplierFilter, products]);

    const getPresentationSortKey = (p) => {
        const pres = (p || '').toUpperCase();
        let group = 2;
        let value = Number.MAX_SAFE_INTEGER;

        if (pres.includes('LECHE BASE')) { group = -1; }
        else if (pres.endsWith('KG')) { group = 0; value = (parseFloat(pres) || 0) * 1000; }
        else if (pres.endsWith('G')) { group = 0; value = parseFloat(pres) || 0; }
        else if (pres.endsWith('L')) { group = 1; value = (parseFloat(pres) || 0) * 1000; }
        else if (pres.endsWith('ML')) { group = 1; value = parseFloat(pres) || 0; }
        else if (pres.endsWith('OZ')) { group = 1; value = parseFloat(pres) || 0; }
        else if (pres === 'STANDARD' || pres === 'STD') { group = 3; }

        return { group, value };
    };

    const comparePresentations = (a, b) => {
        const ka = getPresentationSortKey(a);
        const kb = getPresentationSortKey(b);
        if (ka.group !== kb.group) return ka.group - kb.group;
        return ka.value - kb.value;
    };

    const isSkarchaCategoryName = (name) => {
        const n = String(name || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
        return /^(SKARCHA\s+NO\s+FABRICADOS\s+19%)$/.test(n);
    };

    const getFlavorGroupFromData = (categoryData, flavor, category) => {
        const candidates = [];
        Object.values(categoryData).forEach(pres => {
            const prod = pres[flavor];
            if (prod) candidates.push(prod);
        });

        const detect = (t) => {
            t = String(t || '').toUpperCase();
            if (t.includes('CHAMOY')) return 'CHAMOY';
            if (t.includes('SKARCHALITO')) return 'SKARCHALITO';
            if (['AZUCAR', 'AZUCARES'].some(k => t.includes(k))) return 'AZUCARES';
            if (['AJI', 'SAL', 'TAJIN', 'PICANTE'].some(k => t.includes(k))) return 'SALES';
            return null;
        };

        for (const p of candidates) {
            const g = detect(p.name || flavor);
            if (g) return g;
        }

        if ((candidates.length > 0) && (String(category || '').toUpperCase().includes('SKARCHA'))) {
            return 'AZUCARES';
        }
        return 'OTROS';
    };

    const computeOrderedFlavorsWithDividers = (categoryData, category) => {
        const flavorsSet = new Set();
        Object.values(categoryData).forEach(pres => Object.keys(pres).forEach(fl => flavorsSet.add(fl)));

        const groups = { SALES: [], AZUCARES: [], CHAMOY: [], SKARCHALITO: [], OTROS: [] };
        Array.from(flavorsSet).forEach(fl => {
            const g = getFlavorGroupFromData(categoryData, fl, category);
            (groups[g] || groups.OTROS).push(fl);
        });

        // Calcular conteo de presentaciones por sabor
        const flavorCounts = {};
        flavorsSet.forEach(fl => {
            let count = 0;
            Object.values(categoryData).forEach(pres => {
                if (pres[fl]) count++;
            });
            flavorCounts[fl] = count;
        });

        Object.keys(groups).forEach(k => {
            groups[k].sort((a, b) => {
                // Mayor cantidad de presentaciones primero (descendente)
                const diff = flavorCounts[b] - flavorCounts[a];
                if (diff !== 0) return diff;
                // Si tienen la misma cantidad, alfabético
                return a.localeCompare(b);
            });
        });

        // Ajuste LIMA LIMON
        const az = groups['AZUCARES'];
        const idxLL = az.indexOf('LIMA LIMON');
        if (idxLL !== -1) {
            az.splice(idxLL, 1);
            const idxS = az.indexOf('SANDIA');
            if (idxS !== -1) az.splice(idxS + 1, 0, 'LIMA LIMON');
            else az.push('LIMA LIMON');
        }

        const result = [];
        ['SALES', 'AZUCARES', 'CHAMOY', 'SKARCHALITO', 'OTROS'].forEach(k => {
            if (groups[k].length > 0) {
                if (result.length > 0) result.push('__DIVIDER__');
                result.push(...groups[k]);
            }
        });
        return result;
    };

    const getFlavorIcon = (flavor) => {
        const f = String(flavor).toUpperCase();
        if (f.includes('FRESA')) return '🍓';
        if (f.includes('MANZANA')) return '🍏';
        if (f.includes('SANDIA')) return '🍉';
        if (f.includes('MARACUYA')) return '🍈';
        if (f.includes('UVA')) return '🍇';
        if (f.includes('PIÑA')) return '🍍';
        if (f.includes('COCO')) return '🥥';
        if (f.includes('CEREZA')) return '🍒';
        if (f.includes('LIMON')) return '🍋';
        if (f.includes('NARANJA')) return '🍊';
        if (f.includes('BLUEBERRY') || f.includes('MORA')) return '🫐';
        if (f.includes('DURAZNO')) return '🍑';
        if (f.includes('MANGO')) return '🥭';
        if (f.includes('KIWI')) return '🥝';
        if (f.includes('MENTA')) return '🌿';
        if (f.includes('CHOCOLATE')) return '🍫';
        if (f.includes('VAINILLA')) return '🍦';
        if (f.includes('CAFE')) return '☕';
        if (f.includes('CHICLE')) return '🍬';
        if (f.includes('GOMITA') || f.includes('GOMI')) return '🍬';
        if (f.includes('CHOCOLATE')) return '🍫';
        if (f.includes('NUEZ') || f.includes('MANI') || f.includes('FRUTO SECO') || f.includes('PISTACHO') || f.includes('ARANDANO')) return '🥜';
        if (f.includes('SAL')) return '🧂';
        if (f.includes('PITILLO')) return '🥤';
        if (f.includes('TAPA')) return '🔘';
        if (f.includes('LECHE')) return '🥛';
        if (f.includes('CUCHARA')) return '🥄';
        if (f.includes('JIGGER')) return '⚖️';
        if (f.includes('BAR')) return '🍸';
        if (f.includes('CHILE')) return '🌶️';
        if (f.includes('MERENGON')) return '🧁';
        if (f.includes('BOMBOM') || f.includes('CHUPETA')) return '🍭';
        if (f.includes('REPUESTO') || f.includes('ORING')) return '🔧';
        return '';
    };

    const formatFlavorLabel = (flavor, category) => {
        if (!flavor) return '';
        const f = String(flavor).toUpperCase();
        const cat = String(category || '').toUpperCase();

        // if (f === 'MM') return 'PITILLOS'; // YA NO ES NECESARIO con la nueva extracción
        if (f.includes('OZ-2') || f.includes('2 OZ')) return 'COPAS MEDIDORAS';
        if (f === 'COPAS') return 'BORDEADOR DE COPAS';
        if (f === 'COCTELERA') return 'CUCHARA COCTELERA';
        if (f === 'ESCARCHADOR') return 'JARABE ESCARCHADOR';
        if (f === 'ESTANDARIZADA') return 'LIQUIMON';

        // Reglas específicas para "Productos No fabricados 19%"
        if (cat.includes('PRODUCTOS NO FABRICADOS')) {
            if (f === 'OSITOS') return 'GUDGUMI OSITOS';
            if (f === 'SANDIA') return 'GUDGUMI SANDIA';
            if (f === 'MARACUYA') return 'GUDGUMI MARACUYA';
            if (f === '16') return 'VASOS 16 OZ';
            if (f === '22') return 'VASOS 22 OZ';
            if (f === 'NARANJA') return 'NARANJA DESHIDRATADA';
        }

        // Reglas específicas para "SKARCHA NO FABRICADOS 19%"
        if (cat.includes('SKARCHA NO FABRICADOS')) {
            if (f === '16') return 'VASOS 16 OZ';
            if (f === '22') return 'VASOS 22 OZ';
            if (f === 'NARANJA') return 'NARANJA DESHIDRATADA';
        }

        if (cat.includes('MEZCLA') && f === 'PP') return 'MEZCLA WAFFLES';

        return flavor;
    };

    // --- LOGIC FOR UPDATING & ANALYZING ---

    const handleUpdateConfig = async (productId, config) => {
        try {
            await inventoryManagementService.updateProductConfig(productId, config);
            toast.success('Configuración actualizada');
            fetchInventory(); // Recargar para ver cambios
        } catch (error) {
            console.error('Error updating config:', error);
            toast.error('Error actualizando configuración');
        }
    };

    const handleAnalyzeConsumption = async () => {
        try {
            setAnalyzing(true);
            toast.loading('Analizando consumo...', { id: 'analyze' });
            const response = await inventoryManagementService.analyzeConsumption({
                days: coverageDays,
                leadTime: leadTime
            });
            if (response.success) {
                toast.success(response.message, { id: 'analyze' });
                await fetchInventory();
            } else {
                toast.error(response.message || 'Error analizando consumo', { id: 'analyze' });
            }
        } catch (error) {
            console.error('Error analyzing consumption:', error);
            toast.error('Error al analizar consumo', { id: 'analyze' });
        } finally {
            setAnalyzing(false);
        }
    };

    const handleCalculateABC = async () => {
        try {
            setAnalyzing(true);
            toast.loading('Calculando clasificación ABC...', { id: 'abc' });
            const response = await inventoryManagementService.calculateABC();
            if (response.success) {
                toast.success(response.message, { id: 'abc' });
                await fetchInventory();
            } else {
                toast.error(response.message || 'Error calculando ABC', { id: 'abc' });
            }
        } catch (error) {
            console.error('Error calculando ABC:', error);
            toast.error('Error al calcular clasificación ABC', { id: 'abc' });
        } finally {
            setAnalyzing(false);
        }
    };

    const handleCellClick = (product) => {
        if (product) {
            setSelectedProduct(product);
            setShowModal(true);
        }
    };

    // Determinar color de celda según estado
    // Determinar color de celda según estado
    const getCellColor = (product) => {
        if (!product) return 'bg-gray-50';

        // 1. Si hay análisis de consumo válido (days_until_stockout no es null)
        if (product.days_until_stockout !== null && product.days_until_stockout !== undefined) {
            if (product.days_until_stockout <= 0) return 'bg-red-500 text-white'; // Agotado
            if (product.days_until_stockout <= 3) return 'bg-orange-400 text-white'; // Crítico
            if (product.days_until_stockout <= 7) return 'bg-yellow-300 text-gray-900'; // Bajo
        }

        // 2. Fallback: Si no hay análisis, usar stock vs mínimo
        const minStock = product.min_inventory_qty || 0;
        if (product.current_stock <= 0) return 'bg-red-500 text-white'; // Sin stock
        if (product.current_stock <= minStock) return 'bg-yellow-300 text-gray-900'; // Bajo mínimo

        // 3. Si tiene sugerencia de pedido, resaltar también
        if (product.suggested_order_qty > 0) return 'bg-blue-200 text-blue-900';

        return 'bg-green-100 text-green-900'; // Normal
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            {showFinancials && (
                <div className="w-full mb-6">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        📦 Gestión de Inventario y Reaprovisionamiento
                    </h1>
                    <p className="text-gray-600">
                        Vista matricial de inventario. Haz clic en una celda para ver detalles y configurar.
                    </p>
                </div>
            )}

            {/* Dashboard de KPIs - Solo para admin y cartera */}
            {showFinancials && <InventoryDashboard coverage={coverageDays} leadTime={leadTime} />}

            {/* Desglose por Categoría - Solo para admin y cartera */}
            {showFinancials && (
                <div className="grid grid-cols-5 gap-3 mb-6">
                    {(() => {
                        const grandTotals = { cost: 0, revenue: 0 };
                        const catStats = {};
                        const subcatStats = {};

                        categories.forEach(cat => {
                            const catData = groupedProducts[cat];
                            if (!catData) return;

                            let catCost = 0;
                            let catRevenue = 0;
                            let catStationary = 0;
                            let catDeficit = 0;
                            let catAvgDaily = 0; // Consumo diario promedio total en unidades
                            let catTotalStock = 0; // Stock total en unidades
                            if (!subcatStats[cat]) subcatStats[cat] = {};

                            Object.entries(catData).forEach(([subgroup, presentations]) => {
                                let subCost = 0;
                                let subRevenue = 0;
                                let subUnits = 0;

                                Object.values(presentations).forEach(flavors => {
                                    Object.values(flavors).forEach(product => {
                                        const stock = Number(product.current_stock || 0);
                                        const ivaFactor = 1 + (Number(product.taxes_iva || 19) / 100);
                                        const cost = Number(product.purchasing_price || 0) || (Number(product.standard_price || 0) / ivaFactor);
                                        const revenue = (Number(product.standard_price || 0) / ivaFactor) * stock;
                                        const avgDaily = Number(product.avg_daily_consumption || 0);

                                        // IMPORTANTE: Usar el subgrupo asignado dinámicamente si existe
                                        const effectiveSubgroup = product._assigned_subgroup || subgroup;
                                        if (!subcatStats[cat][effectiveSubgroup]) {
                                            subcatStats[cat][effectiveSubgroup] = { cost: 0, units: 0 };
                                        }

                                        subcatStats[cat][effectiveSubgroup].cost += stock * cost;
                                        subcatStats[cat][effectiveSubgroup].units += stock;

                                        catCost += stock * cost;
                                        catRevenue += revenue;
                                        catAvgDaily += avgDaily;
                                        catTotalStock += stock;

                                        // Dinero Estacionado vs Déficit
                                        const ideal = avgDaily * (leadTime + coverageDays);
                                        const excess = Math.max(0, stock - ideal);
                                        const deficit = Math.max(0, ideal - stock);

                                        catStationary += excess * cost;
                                        catDeficit += deficit * cost;
                                    });
                                });
                            });

                            // Días de inventario = Stock total en unidades / Consumo diario en unidades
                            const catDaysOfInventory = catAvgDaily > 0 ? Math.round(catTotalStock / catAvgDaily) : null;
                            catStats[cat] = {
                                cost: catCost,
                                revenue: catRevenue,
                                stationary: catStationary,
                                deficit: catDeficit,
                                avgDaily: catAvgDaily,
                                totalStock: catTotalStock,
                                inventoryDays: catDaysOfInventory
                            };
                            if (catCost > 0 && cat !== 'SIN CLASIFICAR' && cat !== 'SIN CATEGORIA') {
                                grandTotals.cost += catCost;
                                grandTotals.revenue += catRevenue;
                            }
                        });

                        // 2. Renderizar tarjetas con orden personalizado
                        const CATEGORY_ORDER = [
                            'BUBOLS',
                            'SIROPES',
                            'LIQUIPOPS',
                            'GENIALITY',
                            'HELADOS',
                            'GRANIZADOS',
                            'OTRAS MEZCLAS',
                            'TOPPING',
                            'MATERIAL DE EMPAQUE',
                            'SKARCHA',
                            'SALSAS',
                            'CONOS Y OBLEAS',
                            'ACCESORIOS',
                            'YEXIS',
                            'PRODUCTOS ESPECIALES'
                        ];


                        const sortedCategories = [...categories].sort((a, b) => {
                            const idxA = CATEGORY_ORDER.indexOf(a);
                            const idxB = CATEGORY_ORDER.indexOf(b);

                            if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                            if (idxA !== -1) return -1;
                            if (idxB !== -1) return 1;

                            // Fallback a ventas si no están en la lista (descendente)
                            const costA = catStats[a]?.cost || 0;
                            const costB = catStats[b]?.cost || 0;
                            return costB - costA;
                        });

                        return sortedCategories.map(cat => {
                            const catData = groupedProducts[cat];
                            if (!catData) return null;

                            const totalCostNet = catStats[cat]?.cost || 0;
                            const totalRevNet = catStats[cat]?.revenue || 0;
                            const catMargin = totalRevNet > 0 ? ((totalRevNet - totalCostNet) / totalRevNet) * 100 : 0;
                            const percent = grandTotals.cost > 0 ? (totalCostNet / grandTotals.cost) * 100 : 0;

                            let totalStock = 0;
                            Object.values(catData).forEach(presentations => {
                                Object.values(presentations).forEach(flavors => {
                                    Object.values(flavors).forEach(product => {
                                        totalStock += Number(product.current_stock || 0);
                                    });
                                });
                            });

                            return (
                                <div key={cat} className="bg-white p-4 rounded-lg shadow border border-gray-200 relative overflow-hidden flex flex-col">
                                    {/* Header with Percentage Badge */}
                                    <div className="flex justify-between items-start mb-3">
                                        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide" title={cat}>{cat}</h3>
                                        <span className="text-lg font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded">{percent.toFixed(1)}%</span>
                                    </div>

                                    {/* Main Value and Action Button */}
                                    <div className="flex justify-between items-center mb-3">
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Costo neto • Margen {catMargin.toFixed(1)}%</p>
                                            <p className="text-2xl font-bold text-gray-800">
                                                {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(totalCostNet)}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => handleOpenCategoryDetail(cat)}
                                            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-all font-bold">
                                            VER PRODUCTOS
                                        </button>
                                    </div>

                                    {/* Financial Indicators */}
                                    <div className="space-y-2 mb-3">
                                        {/* Dinero Estacionado */}
                                        {(() => {
                                            const stationaryMoney = catStats[cat]?.stationary || 0;
                                            if (stationaryMoney === 0) return null;
                                            const inventoryDays = catStats[cat]?.inventoryDays || 0;
                                            const colorScheme = getStationaryMoneyColor(inventoryDays);
                                            return (
                                                <div className="flex items-center justify-between py-2 px-3 bg-green-50 rounded">
                                                    <div className="flex items-center gap-2">
                                                        <TrendingUp size={16} className={colorScheme.text} />
                                                        <span className="text-sm text-gray-700 font-medium">Estimado</span>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className={`text-lg font-bold ${colorScheme.text}`}>
                                                            {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(stationaryMoney)}
                                                        </p>
                                                        <p className={`text-xs ${colorScheme.text} uppercase font-bold`}>{colorScheme.severity}</p>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Déficit */}
                                        {(() => {
                                            const deficit = catStats[cat]?.deficit || 0;
                                            if (deficit === 0) return null;
                                            return (
                                                <div className="flex items-center justify-between py-2 px-3 bg-red-50 rounded">
                                                    <div className="flex items-center gap-2">
                                                        <AlertTriangle size={16} className="text-red-600" />
                                                        <span className="text-sm text-gray-700 font- medium">Déficit</span>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-lg font-bold text-red-600">
                                                            {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(deficit)}
                                                        </p>
                                                        <p className="text-xs text-red-600 uppercase font-bold">FALTA INVENTARIO</p>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Balance Neto */}
                                        {(() => {
                                            const net = (catStats[cat]?.stationary || 0) - (catStats[cat]?.deficit || 0);
                                            const getBalanceColor = (balance) => {
                                                if (balance < 0) return { text: 'text-red-600', bg: 'bg-red-50', label: 'REQUIERE INVERSIÓN' };
                                                if (balance > 100000000) return { text: 'text-yellow-600', bg: 'bg-yellow-50', label: 'ADVERTENCIA' };
                                                if (balance > 25000000) return { text: 'text-green-600', bg: 'bg-green-50', label: 'EQUILIBRADO' };
                                                return { text: 'text-emerald-600', bg: 'bg-emerald-50', label: 'CASI EQUILIBRADO' };
                                            };
                                            const balanceScheme = getBalanceColor(net);
                                            return (
                                                <div className={`flex items-center justify-between py-2 px-3 ${balanceScheme.bg} rounded`}>
                                                    <div className="flex items-center gap-2">
                                                        <DollarSign size={16} className={balanceScheme.text} />
                                                        <span className="text-sm text-gray-700 font-medium">Balance</span>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className={`text-lg font-bold ${balanceScheme.text}`}>
                                                            {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(net)}
                                                        </p>
                                                        <p className={`text-xs ${balanceScheme.text} uppercase font-bold`}>{balanceScheme.label}</p>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Días de Inventario */}
                                        {catStats[cat]?.inventoryDays !== null && catStats[cat]?.inventoryDays !== undefined && (() => {
                                            const days = catStats[cat].inventoryDays;
                                            const getRotationLabel = (d) => {
                                                if (d < 22) return 'RIESGO BAJO';
                                                if (d <= 30) return 'ÓPTIMO';
                                                if (d <= 45) return 'EXCESO';
                                                return 'SOBRE-STOCK';
                                            };
                                            // Sincronizado con getStationaryMoneyColor: <22=naranja, 22-30=verde, 31-45=amarillo, >45=rojo
                                            const colorClass = days < 22 ? 'text-orange-600' : days <= 30 ? 'text-green-600' : days <= 45 ? 'text-yellow-600' : 'text-red-600';
                                            const bgClass = days < 22 ? 'bg-orange-50' : days <= 30 ? 'bg-green-50' : days <= 45 ? 'bg-yellow-50' : 'bg-red-50';
                                            return (
                                                <div className={`flex items-center justify-between py-2 px-3 ${bgClass} rounded`}>
                                                    <div className="flex items-center gap-2">
                                                        <Activity size={16} className={colorClass} />
                                                        <span className="text-sm text-gray-700 font-medium">Días Inv</span>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className={`text-lg font-bold ${colorClass}`}>{Math.round(days)} días</p>
                                                        <p className={`text-xs ${colorClass} uppercase font-bold`}>{getRotationLabel(days)}</p>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    {/* Product List */}
                                    <div className="pt-3 border-t border-gray-200">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-xs text-gray-500 font-medium uppercase">Unid: <span className="font-bold text-gray-700">{totalStock}</span></span>
                                            <span className="text-xs text-gray-400">c/IVA: {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(totalCostNet * 1.19)}</span>
                                        </div>
                                        <div className="space-y-1 max-h-[140px] overflow-y-auto custom-scrollbar">
                                            {Object.entries(subcatStats[cat] || {})
                                                .sort(([, a], [, b]) => b.cost - a.cost)
                                                .map(([subName, stats]) => (
                                                    <div key={subName} className="flex items-center justify-between text-sm py-1 hover:bg-gray-50 px-1 rounded">
                                                        <span className="text-gray-700 font-medium truncate flex-1" title={subName}>
                                                            {subName.toLowerCase().replace(/^\w/, c => c.toUpperCase())}
                                                        </span>
                                                        <div className="flex items-center gap-3">
                                                            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">
                                                                {stats.units} un
                                                            </span>
                                                            <span className="font-bold text-gray-800 min-w-[100px] text-right">
                                                                {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(stats.cost)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        });
                    })()}
                </div>
            )}



            {/* Actions Bar */}
            <div className="w-full mb-6 bg-white rounded-lg shadow p-4 sticky top-28 z-20">
                <div className="flex flex-wrap gap-4 items-center justify-between">
                    {showFinancials ? (
                        <div className="flex gap-2 items-center">
                            <label className="text-sm font-medium text-gray-700">Días de cobertura:</label>
                            <select
                                value={coverageDays}
                                onChange={(e) => setCoverageDays(Number(e.target.value))}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value={3}>3 días</option>
                                <option value={7}>7 días</option>
                                <option value={10}>10 días</option>
                                <option value={13}>13 días (Ciclo+LT)</option>
                                <option value={15}>15 días</option>
                                <option value={20}>20 días</option>
                            </select>

                            <label className="text-sm font-medium text-gray-700 ml-2">Espera (entrega):</label>
                            <select
                                value={leadTime}
                                onChange={(e) => setLeadTime(Number(e.target.value))}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value={3}>3 días</option>
                                <option value={5}>5 días (Lun→Sáb)</option>
                                <option value={7}>7 días (c/retraso)</option>
                                <option value={10}>10 días (Festivos)</option>
                            </select>

                            <button
                                onClick={handleAnalyzeConsumption}
                                disabled={analyzing}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                <RefreshCw className={`w-4 h-4 ${analyzing ? 'animate-spin' : ''}`} />
                                {analyzing ? 'Analizando...' : 'Analizar Consumo'}
                            </button>

                            <button
                                onClick={handleCalculateABC}
                                disabled={analyzing}
                                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                <TrendingUp className="w-4 h-4" />
                                Calcular ABC
                            </button>

                            <button
                                onClick={async () => {
                                    toast.loading('Generando Excel...', { id: 'excel' });
                                    const success = await inventoryManagementService.exportToExcel();
                                    if (success) toast.success('Excel descargado', { id: 'excel' });
                                    else toast.error('Error descargando Excel', { id: 'excel' });
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
                            >
                                <FileText className="w-4 h-4" />
                                Exportar Excel
                            </button>

                            <button
                                onClick={async () => {
                                    toast.loading('Generando Reporte de Sobre-Stock...', { id: 'overstock' });
                                    const success = await inventoryManagementService.exportOverstockReport();
                                    if (success) toast.success('Reporte descargado', { id: 'overstock' });
                                    else toast.error('Error generando reporte', { id: 'overstock' });
                                }}
                                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 flex items-center gap-2"
                                title="Descarga productos con exceso de inventario (>45 días)"
                            >
                                <AlertTriangle className="w-4 h-4" />
                                Reporte Sobre-Stock
                            </button>

                            <button
                                onClick={async () => {
                                    if (!supplierFilter) return;
                                    toast.loading(`Generando Orden para ${supplierFilter}...`, { id: 'po' });
                                    const result = await inventoryManagementService.generatePurchaseOrder(supplierFilter);
                                    if (result === true) {
                                        toast.success('Orden de Compra descargada', { id: 'po' });
                                    } else {
                                        toast.error(result?.message || 'Error generando orden', { id: 'po' });
                                    }
                                }}
                                disabled={!supplierFilter}
                                className={`px-4 py-2 text-white rounded-md flex items-center gap-2 ${supplierFilter
                                    ? 'bg-indigo-600 hover:bg-indigo-700'
                                    : 'bg-gray-400 cursor-not-allowed'
                                    }`}
                                title={!supplierFilter ? "Selecciona un proveedor primero" : "Generar Orden de Compra"}
                            >
                                <FileText className="w-4 h-4" />
                                Generar Orden
                            </button>

                            <button
                                onClick={async () => {
                                    toast.loading('Generando OC Consolidada...', { id: 'po-all' });
                                    const success = await inventoryManagementService.generateConsolidatedPurchaseOrder();
                                    if (success) toast.success('OC Consolidada descargada', { id: 'po-all' });
                                    else toast.error('Error generando OC Consolidada', { id: 'po-all' });
                                }}
                                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 flex items-center gap-2"
                                title="Descargar Orden de Compra con TODOS los proveedores"
                            >
                                <FileText className="w-4 h-4" />
                                OC Completa
                            </button>
                        </div>
                    ) : <div />}

                    <div className="flex gap-2 items-center">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Buscar producto..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                            />
                        </div>

                        <select
                            value={abcFilter}
                            onChange={(e) => setAbcFilter(e.target.value)}
                            className="border rounded-md px-3 py-2 text-sm"
                        >
                            <option value="">Todas las Clases ABC</option>
                            <option value="A">Clase A (Alta Rotación)</option>
                            <option value="B">Clase B (Media Rotación)</option>
                            <option value="C">Clase C (Baja Rotación)</option>
                        </select>

                        {/* Filtro Proveedor */}
                        <select
                            value={supplierFilter}
                            onChange={(e) => setSupplierFilter(e.target.value)}
                            className="border rounded-md px-3 py-2 text-sm"
                        >
                            <option value="">Todos los Proveedores</option>
                            {uniqueSuppliers.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Matrix View */}
            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                    <p className="ml-2 text-gray-600">Cargando inventario...</p>
                </div>
            ) : (
                <>
                    <div className="space-y-12 w-full">
                        {categories.map(group => {
                            const groupData = groupedProducts[group];
                            // Si groupData es undefined (por seguridad), saltar
                            if (!groupData) return null;

                            // Definir orden específico de subgrupos
                            // Definir orden específico de subgrupos
                            const SUBGROUP_ORDER = [
                                // Prioridad Alta Solicitada
                                'Perlas Bubols',
                                'Perlas Liqui Pop',
                                'Sirope Bubols',
                                'Sirope Geniality',
                                'Sirope Fusion Mix',
                                'GRANIZADOS',
                                'CREMOSO',
                                'CITRICO',
                                'DULCE',
                                'BASE',
                                'Helado Yogurt',
                                'Topping Chocolate',
                                'Topping Gomas',
                                'Topping Frutos Secos',
                                'Topping Dulce',
                                'VASOS',
                                'TAPAS',
                                'CONTENEDOR',
                                'CUCHARA',
                                'Azucar',
                                'Sal Sabor',
                                // Resto General
                                'GOLOSINAS Y POSTRES',
                                'GRANIZADOS Y MIXES',
                                'COCTELERÍA Y BEBIDAS',
                                'HERRAMIENTAS Y UTENSILIOS',
                                'LÁCTEOS',
                                'REPUESTOS Y MANTENIMIENTO',
                                'MALTEADAS',
                                'CHANTILLY',
                                'ROLL FACTORY',
                                'YOGUR SIN AZUCAR',
                                'HELADO SUAVE',
                                'SUAVE SIN AZUCAR',
                                'HELADO PREMIUM',
                                'FRAPPE',
                                'MEZCLAS',
                                'MALTEADA'
                            ];

                            const subgroups = Object.keys(groupData).sort((a, b) => {
                                const idxA = SUBGROUP_ORDER.indexOf(a);
                                const idxB = SUBGROUP_ORDER.indexOf(b);

                                // Si ambos están en la lista, usar el orden de la lista
                                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                                // Si solo A está en la lista, va primero
                                if (idxA !== -1) return -1;
                                // Si solo B está en la lista, va primero
                                if (idxB !== -1) return 1;
                                // Si ninguno está, alfabético
                                return a.localeCompare(b);
                            });

                            return (
                                <div key={group} className="space-y-6">
                                    {/* Encabezado del Grupo (Ficha Grande) */}
                                    <div className="flex items-center gap-4 border-b-2 border-gray-300 pb-2 mb-6">
                                        <h2 className="text-3xl font-extrabold text-gray-800 uppercase tracking-tight">
                                            {group}
                                        </h2>
                                        <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded-full">
                                            {subgroups.length} Subgrupos
                                        </span>
                                    </div>

                                    {/* Iterar Subgrupos */}
                                    {subgroups.map(subgroup => {
                                        const subgroupData = groupData[subgroup];

                                        // Obtener todos los sabores/variantes únicos de este subgrupo para las columnas
                                        const allFlavors = [...new Set(
                                            Object.values(subgroupData).flatMap(pres => Object.keys(pres))
                                        )].sort((a, b) => {
                                            // Lógica para enviar la columna de LECHE al final
                                            const isMilkA = String(a).startsWith('LECHE (MEL');
                                            const isMilkB = String(b).startsWith('LECHE (MEL');

                                            if (isMilkA && !isMilkB) return 1;
                                            if (!isMilkA && isMilkB) return -1;
                                            return a.localeCompare(b);
                                        });

                                        return (
                                            <div key={subgroup} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
                                                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                                                    <h3 className="text-lg font-bold text-gray-700 uppercase tracking-wide">
                                                        {subgroup}
                                                    </h3>
                                                </div>

                                                <div className="overflow-x-auto">
                                                    <table className="w-full table-auto border-collapse">
                                                        <thead>
                                                            <tr>
                                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider sticky left-0 bg-white z-20 border-b border-r border-gray-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] min-w-[120px]">
                                                                    PRES
                                                                </th>
                                                                {/* Columna TOTAL */}
                                                                <th className="px-3 py-3 text-center text-xs font-extrabold text-indigo-700 uppercase tracking-wider min-w-[80px] border-b border-r-2 border-indigo-200 bg-indigo-50 sticky left-[120px] z-20">
                                                                    TOTAL
                                                                </th>
                                                                {allFlavors.map((flavor, idx) => (
                                                                    <th key={idx} className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] border-b border-gray-100">
                                                                        <div className="flex flex-col items-center gap-1">
                                                                            <span className="text-xl filter drop-shadow-sm">{getFlavorIcon(flavor)}</span>
                                                                            <span className="text-[10px] font-bold leading-tight text-gray-600 max-w-[90px]">
                                                                                {flavor}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-100">
                                                            {Object.keys(subgroupData).sort((a, b) => comparePresentations(a, b)).map(presentation => {
                                                                const isLecheRow = presentation.includes('LECHE');
                                                                const rowBgClass = isLecheRow ? 'bg-blue-50' : '';

                                                                return (
                                                                    <tr key={presentation} className={rowBgClass}>
                                                                        <td className={`px-4 py-3 whitespace-nowrap text-xs ${isLecheRow ? 'font-extrabold text-blue-900' : 'font-bold text-gray-900'} sticky left-0 ${isLecheRow ? 'bg-blue-100' : 'bg-white'} z-20 border-r border-gray-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`}>
                                                                            {presentation}
                                                                        </td>
                                                                        {/* Celda TOTAL */}
                                                                        <td className={`px-3 py-3 text-center text-sm font-extrabold border-r-2 border-indigo-200 sticky left-[120px] z-20 ${isLecheRow ? 'bg-indigo-100 text-indigo-900' : 'bg-indigo-50 text-indigo-700'}`}>
                                                                            {(() => {
                                                                                const total = allFlavors.reduce((sum, flavor) => {
                                                                                    const product = subgroupData[presentation][flavor];
                                                                                    return sum + (product?.current_stock || 0);
                                                                                }, 0);
                                                                                return total > 0 ? total : '-';
                                                                            })()}
                                                                        </td>
                                                                        {allFlavors.map((flavor, idx) => {
                                                                            const product = subgroupData[presentation][flavor];

                                                                            if (!product) {
                                                                                return <td key={idx} className="px-2 py-3 text-center border-b border-gray-50 bg-gray-50/50"></td>;
                                                                            }

                                                                            // Colores estilo semáforo
                                                                            const cellColor = getCellColor(product);
                                                                            let textClass = 'text-gray-900';
                                                                            if (cellColor.includes('bg-red')) textClass = 'text-white';
                                                                            else if (cellColor.includes('bg-blue')) textClass = 'text-blue-900';
                                                                            else if (cellColor.includes('bg-green')) textClass = 'text-emerald-900';

                                                                            return (
                                                                                <td
                                                                                    key={idx}
                                                                                    onClick={() => handleCellClick(product)}
                                                                                    title={`${product.name}\nCódigo: ${product.internal_code}\nStock: ${product.current_stock}`}
                                                                                    className={`px-2 py-3 text-center cursor-pointer transition-colors duration-200 border-b border-gray-100 hover:brightness-95 ${cellColor}`}
                                                                                >
                                                                                    <div className="flex flex-col items-center justify-center h-full relative">
                                                                                        <span className={`text-lg font-bold leading-none ${textClass}`}>{product.current_stock}</span>

                                                                                        {/* Info Stock/Pedido */}
                                                                                        {product.days_until_stockout !== null && product.days_until_stockout <= 7 && (
                                                                                            <span className="text-[9px] opacity-80 mt-1 font-semibold block">
                                                                                                {product.days_until_stockout <= 0 ? 'Agotado' : `${product.days_until_stockout}d`}
                                                                                            </span>
                                                                                        )}
                                                                                        {product.suggested_order_qty > 0 ? (
                                                                                            <span className="mt-1 px-1.5 py-0.5 bg-white/40 rounded text-[9px] font-bold shadow-sm backdrop-blur-sm block">
                                                                                                Pedir: {product.suggested_order_qty}
                                                                                            </span>
                                                                                        ) : (
                                                                                            <span className="text-[8px] opacity-60 block mt-1">
                                                                                                Min:{product.min_inventory_qty}
                                                                                            </span>
                                                                                        )}

                                                                                        {/* Price Indicator */}
                                                                                        {product.purchasing_price > 0 && (
                                                                                            <div className="absolute top-[-4px] right-[-4px] bg-green-600 text-white rounded-full w-3 h-3 flex items-center justify-center text-[7px]" title={`$${product.purchasing_price}`}>
                                                                                                $
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </td>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </>
            )
            }

            {/* Modal de Detalles de Producto */}
            {
                showModal && selectedProduct && (
                    <ProductDetailsModal
                        product={selectedProduct}
                        onClose={() => setShowModal(false)}
                        onUpdate={handleUpdateConfig}
                        onAnalyze={handleAnalyzeConsumption}
                        readOnly={!showFinancials}
                    />
                )
            }

            {/* Modal de Detalle de Categoría */}
            {showCategoryModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-xl">
                            <div>
                                <h2 className="text-xl font-bold text-gray-800">Detalle de Inventario: {selectedCategoryData.name}</h2>
                                <p className="text-xs text-gray-500">Listado de productos y valoración actual</p>
                            </div>
                            <button onClick={() => setShowCategoryModal(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b-2 border-gray-100">
                                        <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase">Producto</th>
                                        <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase text-right">Costo Neto</th>
                                        <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase text-center">Stock</th>
                                        <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase text-right">Valor Inv.</th>
                                        <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase text-center">Días Stock</th>
                                        <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase text-right">Dinero Estac.</th>
                                        <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase text-right">Déficit</th>
                                        <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase text-right">Rent. Op.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedCategoryData.items.map((item, idx) => (
                                        <tr key={item.id} className={`hover:bg-blue-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                                            <td className="py-3 px-4">
                                                <div className="font-medium text-gray-800 text-sm">{item.name}</div>
                                                <div className="text-[10px] text-gray-400 font-mono">{item.internal_code}</div>
                                            </td>
                                            <td className="py-3 px-4 text-sm text-gray-600 text-right">
                                                {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(item.costNet)}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${item.current_stock <= 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                                    {item.current_stock}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-sm font-bold text-gray-800 text-right">
                                                {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(item.inventoryValue)}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className={`text-xs font-medium ${item.stockDays !== null && item.stockDays < 15 ? 'text-red-600 font-bold' : item.stockDays > 45 ? 'text-orange-600 font-bold' : 'text-gray-600'}`}>
                                                    {item.stockDays !== null ? `${Math.round(item.stockDays)}d` : '-'}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                {item.stationaryMoney > 0 ? (
                                                    <div className="flex flex-col items-end">
                                                        <span className="text-xs font-bold text-orange-600">
                                                            {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(item.stationaryMoney)}
                                                        </span>
                                                        <span className="text-[8px] text-gray-400 font-mono">Exc: {Math.round(item.excessQty)}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-300">-</span>
                                                )}
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                {item.deficitMoney > 0 ? (
                                                    <div className="flex flex-col items-end">
                                                        <span className="text-xs font-bold text-red-600">
                                                            {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(item.deficitMoney)}
                                                        </span>
                                                        <span className="text-[8px] text-gray-400 font-mono">Fal: {Math.round(item.deficitQty)}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-300">-</span>
                                                )}
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <span className={`text-xs font-bold ${item.gmroi > 30 ? 'text-green-600' : item.gmroi < 10 ? 'text-red-600' : 'text-blue-600'}`}>
                                                    {item.gmroi.toFixed(1)}%
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl flex flex-wrap justify-between items-center gap-4">
                            <div className="flex gap-6">
                                <div className="flex flex-col">
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">Total Valor</span>
                                    <span className="text-lg font-black text-gray-800">
                                        {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(
                                            selectedCategoryData.items.reduce((sum, item) => sum + item.inventoryValue, 0)
                                        )}
                                    </span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">Estacionado</span>
                                    <span className="text-lg font-black text-orange-600">
                                        {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(
                                            selectedCategoryData.items.reduce((sum, item) => sum + item.stationaryMoney, 0)
                                        )}
                                    </span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">Déficit Total</span>
                                    <span className="text-lg font-black text-red-600">
                                        {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(
                                            selectedCategoryData.items.reduce((sum, item) => sum + item.deficitMoney, 0)
                                        )}
                                    </span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">Balance Neto</span>
                                    <span className={`text-lg font-black ${(selectedCategoryData.items.reduce((sum, item) => sum + item.stationaryMoney, 0) - selectedCategoryData.items.reduce((sum, item) => sum + item.deficitMoney, 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(
                                            selectedCategoryData.items.reduce((sum, item) => sum + item.stationaryMoney, 0) - selectedCategoryData.items.reduce((sum, item) => sum + item.deficitMoney, 0)
                                        )}
                                    </span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">Rent. Op. Grupo</span>
                                    <span className="text-lg font-black text-blue-700">
                                        {(selectedCategoryData.items.reduce((sum, item) => sum + (item.gmroi * item.inventoryValue), 0) /
                                            Math.max(1, selectedCategoryData.items.reduce((sum, item) => sum + item.inventoryValue, 0))).toFixed(1)}%
                                    </span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">Promedio Días</span>
                                    <span className="text-lg font-black text-indigo-700">
                                        {Math.round(selectedCategoryData.items.filter(i => i.stockDays !== null).reduce((sum, i) => sum + i.stockDays, 0) /
                                            Math.max(1, selectedCategoryData.items.filter(i => i.stockDays !== null).length))}d
                                    </span>
                                </div>
                            </div>
                            <span className="text-gray-400 text-xs font-medium bg-gray-100 px-3 py-1 rounded-full">{selectedCategoryData.items.length} productos</span>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default InventoryManagementPage;
